// Push通知に関するStateの管理
// デバイス単位で保持する情報のため同一UserIdでも異なる可能性がある
// クライアント側で設定し、サーバへ送信するが、サーバからユーザの設定した情報を取得することはしない
// そのため UserState の中に含めず 独立した State として管理する
// 永続化は localStorage を利用する
// IndexedDB は容量が大きいが、Push通知の設定情報は小さいため localStorage で十分
// また IndexedDB は非同期処理になるため、アプリ起動時に即座に取得できない場合がある
// そのため IndexedDB よりも即座に取得できる localStorage を利用している
import type { PushState } from "~/types/entity/push";

const stateName = "push";
const keyName = () => "pushInfo";
const getLocalValue = (): PushState => {
  if (import.meta.client) {
    const localVal = localStorage.getItem(keyName());
    if (localVal) {
      try {
        return JSON.parse(localVal) as PushState; // TODO(as-cast): 強制型キャスト。安全な型付けへ置換を検討
      }
      catch {
        // 解析できなかった場合は localStorage の情報を削除する
        localStorage.removeItem(keyName());
      }
    }
  }
  return {
    // アクセスを試みていない未定義状態の userInfo
    push: {
      enabled: false,
      activity: "day"
    },
    deviceId: undefined,
  } satisfies PushState;
};

export const usePushState = (): {
  get: () => Ref<PushState>
  /**
   * Stateにあればそれを採用するが、なかった場合で
   * かつ クライアント(Webブラウザ) の場合 localStorage の永続化情報を二次的に取得する
   */
  getPersisted: () => Promise<PushState>
  set: (v: PushState) => Promise<void>
  getDatabaseName: () => string
} => {
  const state = useState<PushState>(stateName, (): PushState => getLocalValue());

  if (import.meta.client) {
    return ({
      get: () => state,
      getPersisted: async () => {
        const stateValue = state.value;
        if (stateValue.deviceId) {
          return stateValue;
        }
        else {
          const localVal = getLocalValue();
          state.value = localVal;
          return localVal;
        }
      },
      set: async (v: PushState) => {
        state.value = v;
        // 登録は永続化ストレージを毎回更新する
        if (import.meta.client) {
          localStorage.setItem(keyName(), JSON.stringify(v));
        }
      },
      getDatabaseName: () => keyName(),
    });
  }
  else {
    // サーバー側
    return ({
      get: () => state,
      getPersisted: async () => {
        return state.value;
      },
      set: async (v: PushState) => {
        state.value = v;
      },
      getDatabaseName: () => keyName(),
    });
  }
};
